import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { viewDocument } from "../utiles/DocumentService";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";

import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

import MenuCard from "./MenuCard";

const SIDEBAR_COLOR = "#123B5D";
const SIDEBAR_ACTIVE = "#1E557D";
const SIDEBAR_HOVER = "rgba(255,255,255,0.08)";
const SIDEBAR_TEXT = "#FFFFFF";
const SIDEBAR_MUTED = "rgba(255,255,255,0.70)";
const SIDEBAR_DIVIDER = "rgba(255,255,255,0.12)";

const DRAWER_WIDTH = 280;

const categoryIcons = {
  Dashboard: <DashboardOutlinedIcon />,
  "Inspection Management": <FactCheckOutlinedIcon />,
  "Audit Management": <GavelOutlinedIcon />,
  "Safety Management": <HealthAndSafetyOutlinedIcon />,
  "Safety Log": <HealthAndSafetyOutlinedIcon />,
  "Permit To Work": <VerifiedUserOutlinedIcon />,
  "Document & Template Management": <DescriptionOutlinedIcon />,
  "People Management": <GroupsOutlinedIcon />,
  "Organization & Configuration": <SettingsOutlinedIcon />,

  // Existing category names
  Master: <BusinessOutlinedIcon />,
  Parivikshak: <WorkOutlineIcon />,

  // Fallback icon instead of GoDotFill
  _default: <BusinessOutlinedIcon />,
};

/**
 * Props:
 *  - sidebarOpen : boolean
 *  - onClose     : () => void   (used on mobile to close the drawer;
 *                                e.g. onClose={() => setSidebarOpen(false)})
 */
const MyProSidebar = ({ sidebarOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // < 900px

  const [groupedData, setGroupedData] = useState({});
  const [openCategory, setOpenCategory] = useState(null);

  const [org, setOrg] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  const orgName = org?.name || "Organisation";
  const orgInitials =
    orgName
      .replace(/[^a-zA-Z]/g, "")
      .slice(0, 2)
      .toUpperCase() || "OR";

  useEffect(() => {
    const functions = JSON.parse(localStorage.getItem("functions")) || [];

    const grouped = functions.reduce((acc, item) => {
      const category = item.category || "Other";
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {});
    setGroupedData(grouped);

    // Organisation details
    let orgData = null;
    try {
      orgData = JSON.parse(localStorage.getItem("org"));
    } catch {
      orgData = null;
    }
    setOrg(orgData);

    // Logo (only if the org has one)
    if (!orgData?.logo) return;

    const orgcode = localStorage.getItem("custCode") || "ptw";
    const envCode = localStorage.getItem("envcode") || "Prod";
    let cancelled = false;

    (async () => {
      try {
        const folderPath = `${orgcode}/org_${orgData.code}`;
        const response = await viewDocument({
          projectCode: "Ptw",
          envCode,
          folderPath,
          fileName: orgData.logo,
        });
        if (!response || response?.cd === 1) return;

        const lower = orgData.logo.toLowerCase();
        const mime = lower.endsWith(".png")
          ? "image/png"
          : lower.endsWith(".gif")
            ? "image/gif"
            : lower.endsWith(".svg")
              ? "image/svg+xml"
              : "image/jpeg";

        if (!cancelled) setLogoUrl(`data:${mime};base64,${response}`);
      } catch (err) {
        console.error("Sidebar logo fetch error:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Close the overlay drawer after choosing something (mobile only)
  const closeIfMobile = () => {
    if (isMobile && typeof onClose === "function") onClose();
  };

  const handleLogout = () => {
    localStorage.clear();

    navigate("/login", {
      replace: true,
    });

    window.location.reload();
  };

  const paperWidth = isMobile
    ? `min(${DRAWER_WIDTH}px, 85vw)`
    : sidebarOpen
      ? DRAWER_WIDTH
      : 0;

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? !!sidebarOpen : true}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isMobile ? "auto" : sidebarOpen ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        ...(isMobile && { zIndex: (t) => Math.max(t.zIndex.drawer, 1400) }),

        "& .MuiDrawer-paper": {
          width: paperWidth,
          maxWidth: "100vw",
          height: "100dvh",

          boxSizing: "border-box",

          backgroundColor: SIDEBAR_COLOR,
          backgroundImage: "none",

          color: SIDEBAR_TEXT,

          display: "flex",
          flexDirection: "column",

          borderRight: "none",

          overflowX: "hidden",

          visibility: isMobile || sidebarOpen ? "visible" : "hidden",

          transition: "width 220ms ease, visibility 220ms ease",
        },
      }}
    >
      <Box
        onClick={() => {
          navigate("/workitemdashboard");
          closeIfMobile();
        }}
        sx={{
          height: { xs: 96, md: 115 },
          minHeight: { xs: 96, md: 115 },
          flexShrink: 0, // add this
          pt: "env(safe-area-inset-top)", // optional: avoids phone notch overlap
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt={orgName}
            onError={() => setLogoUrl(null)}
            sx={{
              width: { xs: 50, md: 58 },
              height: { xs: 50, md: 58 },
              borderRadius: "50%",
              objectFit: "contain",
              backgroundColor: "#FFFFFF",
              border: "2px solid rgba(255,255,255,0.25)",
            }}
          />
        ) : (
          <Box
            sx={{
              width: { xs: 50, md: 58 },
              height: { xs: 50, md: 58 },
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: SIDEBAR_ACTIVE,
              border: "2px solid rgba(255,255,255,0.25)",
              color: SIDEBAR_TEXT,
              fontSize: { xs: "18px", md: "20px" },
              fontWeight: 700,
              letterSpacing: "1px",
            }}
          >
            {orgInitials}
          </Box>
        )}

        <Typography
          title={orgName}
          sx={{
            mt: 1,
            fontSize: "14px",
            fontWeight: 600,
            color: SIDEBAR_TEXT,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
          }}
        >
          {orgName}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: SIDEBAR_DIVIDER }} />

      {/* ---------- Menu ---------- */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,

          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",

          px: 0.75,
          py: 1.5,

          /* Scrollbar */
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255,255,255,0.18)",
            borderRadius: "10px",
          },
        }}
      >
        <List disablePadding>
          {Object.entries(groupedData).map(([category, items]) => {
            const categoryOpen = openCategory === category;

            const categoryHasActiveItem = items.some(
              (item) => location.pathname === item.launch,
            );

            return (
              <Box
                key={category}
                sx={{
                  mb: { xs: 1.25, md: 1.5 }, // more space between main categories
                }}
              >
                <ListItemButton
                  onClick={() =>
                    setOpenCategory(categoryOpen ? null : category)
                  }
                  sx={{
                    minHeight: { xs: 46, md: 44 },

                    px: 1.5,

                    borderRadius: "7px",

                    position: "relative",

                    color: SIDEBAR_TEXT,

                    backgroundColor:
                      categoryHasActiveItem && !categoryOpen
                        ? "rgba(255,255,255,0.05)"
                        : "transparent",

                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.08)",
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color: SIDEBAR_MUTED,
                      "& svg": { fontSize: 18 },
                    }}
                  >
                    {categoryIcons[category] || categoryIcons._default}
                  </ListItemIcon>

                  <ListItemText
                    primary={category}
                    primaryTypographyProps={{
                      fontSize: "13.5px",
                      fontWeight: categoryHasActiveItem ? 600 : 500,
                      color: SIDEBAR_TEXT,
                      noWrap: true,
                    }}
                  />

                  {categoryOpen ? (
                    <ExpandLess sx={{ fontSize: 18, color: SIDEBAR_MUTED }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: 18, color: SIDEBAR_MUTED }} />
                  )}
                </ListItemButton>

                <Collapse in={categoryOpen} timeout={180} unmountOnExit>
                  <Box
                    sx={{
                      position: "relative",
                      ml: 2.75,
                      pl: 1,
                      mt: 0.5,
                      borderLeft: "1px solid rgba(255,255,255,0.10)",
                    }}
                  >
                    <List disablePadding>
                      {items.map((menuItem) => {
                        const isActive = location.pathname === menuItem.launch;

                        return (
                          <ListItemButton
                            key={menuItem.id}
                            component={Link}
                            to={menuItem.launch}
                            onClick={closeIfMobile}
                            selected={isActive}
                            sx={{
                              minHeight: { xs: 42, md: 36 },

                              px: 1.25,
                              mb: 0.25,

                              borderRadius: "6px",
                              position: "relative",

                              color: isActive
                                ? "#FFFFFF"
                                : "rgba(255,255,255,0.68)",

                              backgroundColor: isActive
                                ? SIDEBAR_ACTIVE
                                : "transparent",

                              "&:hover": {
                                backgroundColor: isActive
                                  ? SIDEBAR_ACTIVE
                                  : "rgba(255,255,255,0.06)",
                                color: "#FFFFFF",
                              },

                              "&.Mui-selected": {
                                backgroundColor: SIDEBAR_ACTIVE,
                                color: "#FFFFFF",
                              },

                              "&.Mui-selected:hover": {
                                backgroundColor: SIDEBAR_ACTIVE,
                              },

                              ...(isActive && {
                                "&::before": {
                                  content: '""',
                                  position: "absolute",
                                  left: 0,
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  width: "3px",
                                  height: "18px",
                                  borderRadius: "0 3px 3px 0",
                                  backgroundColor: "#FFFFFF",
                                },
                              }),
                            }}
                          >
                            <Box
                              sx={{
                                width: 5,
                                height: 5,
                                flexShrink: 0,
                                borderRadius: "50%",
                                mr: 1.5,
                                backgroundColor: isActive
                                  ? "#FFFFFF"
                                  : "rgba(255,255,255,0.55)",
                              }}
                            />

                            <ListItemText
                              primary={menuItem.functionality}
                              primaryTypographyProps={{
                                fontSize: "12.5px",
                                fontWeight: isActive ? 600 : 400,
                                lineHeight: 1.3,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </List>
      </Box>

      <Divider sx={{ borderColor: SIDEBAR_DIVIDER }} />

      {/* ---------- Footer: logout + menu card ---------- */}
      <Box
        sx={{
          px: 1,
          pt: 0.75,
          pb: 1,
          flexShrink: 0,
        }}
      >
        <List disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              minHeight: 44,
              borderRadius: "8px",
              color: SIDEBAR_TEXT,
              "&:hover": { backgroundColor: SIDEBAR_HOVER },
            }}
          >
            <ListItemIcon sx={{ minWidth: 38, color: SIDEBAR_TEXT }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>

            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                fontSize: "14px",
                fontWeight: 500,
              }}
            />
          </ListItemButton>
        </List>

        <MenuCard />
      </Box>
    </Drawer>
  );
};

export default MyProSidebar;
